import React, { useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { CreditCard as CreditCardIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { AuthContext } from '../App';

const Signup: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  if (auth?.user) {
    return <Navigate to="/cards" replace />;
  }

  // Real-time validation helper
  const validateField = (fieldName: string, value: string): string | null => {
    switch (fieldName) {
      case 'firstName':
        if (!value.trim()) return 'First name is required.';
        if (/\d/.test(value)) return 'First name cannot contain numbers.';
        return null;
      case 'lastName':
        if (!value.trim()) return 'Last name is required.';
        if (/\d/.test(value)) return 'Last name cannot contain numbers.';
        return null;
      case 'username':
        if (!value.trim()) return 'Username is required.';
        const trimmedUsername = value.trim();
        if (trimmedUsername.length < 3) return 'Username must be at least 3 characters long.';
        if (trimmedUsername.length > 150) return 'Username must not exceed 150 characters.';
        // Username cannot contain numbers - only letters, underscores, and hyphens
        if (/\d/.test(trimmedUsername)) {
          return 'Username cannot contain numbers. Only letters, underscores, and hyphens are allowed.';
        }
        // Only allow letters, underscores, and hyphens
        if (!/^[a-zA-Z_-]+$/.test(trimmedUsername)) {
          return 'Username can only contain letters, underscores, and hyphens.';
        }
        // Cannot start or end with underscore or hyphen
        if (trimmedUsername.startsWith('_') || trimmedUsername.startsWith('-') || 
            trimmedUsername.endsWith('_') || trimmedUsername.endsWith('-')) {
          return 'Username cannot start or end with underscore or hyphen.';
        }
        return null;
      case 'email':
        if (!value.trim()) return 'Email is required.';
        const normalizedEmail = value.trim().toLowerCase();
        if (normalizedEmail.length > 254) return 'Email must not exceed 254 characters.';
        if (normalizedEmail.split('@').length - 1 !== 1) return 'Email must contain a single "@" symbol.';
        const [localPart, domainPart] = normalizedEmail.split('@');
        if (!localPart || !domainPart) return 'Email must have both local and domain parts.';
        if (localPart.length > 64) return 'Email local part must not exceed 64 characters.';
        if (!/^[a-z0-9._%+-]+$/.test(localPart)) {
          return "Email local part contains invalid characters. Allowed: letters, numbers, '.', '_', '%', '+', '-'.";
        }
        if ('._%+-'.includes(localPart[0]) || '._%+-'.includes(localPart[localPart.length - 1])) {
          return "Email local part cannot start or end with '.', '_', '%', '+' or '-'.";
        }
        if (normalizedEmail.includes('..')) return 'Email cannot contain consecutive dots.';
        if (!domainPart.includes('.')) return 'Email domain must contain at least one dot (e.g., example.com).';
        return null;
      case 'confirmEmail':
        // Confirm email is optional - only validate if user enters something
        // If empty, it's valid (optional field)
        if (!value.trim()) return null; // Optional field, no error if empty
        // If user entered something, it must match the email
        if (email && value.trim().toLowerCase() !== email.trim().toLowerCase()) {
          return 'Email and confirm email must match.';
        }
        return null;
      case 'password':
        if (value.length < 8) return 'Password must be at least 8 characters long.';
        if (value.includes(' ')) return 'Password must not contain spaces.';
        if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter.';
        if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter.';
        if (!/\d/.test(value)) return 'Password must contain at least one digit.';
        if (!/[!@#$%^&*(),.?":{}|<>_\-+=/\\[\];']/.test(value)) {
          return 'Password must contain at least one special character.';
        }
        return null;
      case 'confirmPassword':
        if (!value) return 'Please confirm your password.';
        if (password && value !== password) return 'Passwords do not match.';
        return null;
      default:
        return null;
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, value);
    if (error) {
      setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFieldChange = (fieldName: string, value: string, setter: (value: string) => void) => {
    setter(value);
    // Clear error when user starts typing
    if (fieldErrors[fieldName] && touchedFields[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    // Real-time validation for email confirmation when email changes
    if (fieldName === 'email' && confirmEmail) {
      const confirmError = validateField('confirmEmail', confirmEmail);
      if (confirmError) {
        setFieldErrors(prev => ({ ...prev, confirmEmail: confirmError }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmEmail;
          return newErrors;
        });
      }
    }
    // Real-time validation for password confirmation
    if (fieldName === 'password' && confirmPassword) {
      const confirmError = validateField('confirmPassword', confirmPassword);
      if (confirmError) {
        setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    // Mark required fields as touched (confirmEmail is optional, so only mark if user entered something)
    setTouchedFields({
      firstName: true,
      lastName: true,
      username: true,
      email: true,
      confirmEmail: confirmEmail.trim().length > 0, // Only mark as touched if user entered something
      password: true,
      confirmPassword: true,
    });

    const newFieldErrors: Record<string, string> = {};

    // First name: required, cannot contain numbers
    if (!firstName.trim()) {
      newFieldErrors.firstName = 'First name is required.';
    } else if (/\d/.test(firstName)) {
      newFieldErrors.firstName = 'First name cannot contain numbers.';
    }

    // Last name: required, cannot contain numbers
    if (!lastName.trim()) {
      newFieldErrors.lastName = 'Last name is required.';
    } else if (/\d/.test(lastName)) {
      newFieldErrors.lastName = 'Last name cannot contain numbers.';
    }

    // Username: validate (no numbers, proper format)
    const usernameError = validateField('username', username);
    if (usernameError) {
      newFieldErrors.username = usernameError;
    }

    // Email: apply strong client-side validation (format + length)
    const rawEmail = email.trim();
    if (!rawEmail) {
      newFieldErrors.email = 'Email is required.';
    } else {
      const normalizedEmail = rawEmail.toLowerCase();
      if (normalizedEmail.length > 254) {
        newFieldErrors.email = 'Email must not exceed 254 characters.';
      } else if (normalizedEmail.split('@').length - 1 !== 1) {
        newFieldErrors.email = 'Email must contain a single "@" symbol.';
      } else {
        const [localPart, domainPart] = normalizedEmail.split('@');
        if (!localPart || !domainPart) {
          newFieldErrors.email = 'Email must have both local and domain parts.';
        } else if (localPart.length > 64) {
          newFieldErrors.email = 'Email local part must not exceed 64 characters.';
        } else if (!/^[a-z0-9._%+-]+$/.test(localPart)) {
          newFieldErrors.email =
            "Email local part contains invalid characters. Allowed: letters, numbers, '.', '_', '%', '+', '-'.";
        } else if ('._%+-'.includes(localPart[0]) || '._%+-'.includes(localPart[localPart.length - 1])) {
          newFieldErrors.email =
            "Email local part cannot start or end with '.', '_', '%', '+' or '-'.";
        } else if (normalizedEmail.includes('..')) {
          newFieldErrors.email = 'Email cannot contain consecutive dots.';
        } else if (!domainPart.includes('.')) {
          newFieldErrors.email = 'Email domain must contain at least one dot (e.g., example.com).';
        }
      }
    }

    // Confirm email: optional - only validate if user entered something
    // If user entered confirm email, it must match the email
    if (confirmEmail.trim()) {
      const confirmEmailError = validateField('confirmEmail', confirmEmail);
      if (confirmEmailError) {
        newFieldErrors.confirmEmail = confirmEmailError;
      }
    }

    // Password: strong client-side validation mirroring backend
    if (password.length < 8) {
      newFieldErrors.password = 'Password must be at least 8 characters long.';
    } else if (password.includes(' ')) {
      newFieldErrors.password = 'Password must not contain spaces.';
    } else {
      if (!/[A-Z]/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one uppercase letter.';
      } else if (!/[a-z]/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one lowercase letter.';
      } else if (!/\d/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one digit.';
      } else if (!/[!@#$%^&*(),.?":{}|<>_\-+=/\\[\];']/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one special character.';
      }
    }

    if (password !== confirmPassword) {
      newFieldErrors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fix the highlighted fields and try again.');
      return;
    }

    setLoading(true);
    try {
      // Use authService.register via context login/loader
      const { authService } = await import('../services/auth');
      // Send confirmEmail only if user provided it (optional field)
      const response = await authService.register(
        username.trim(),
        email.trim().toLowerCase(),
        confirmEmail.trim() ? confirmEmail.trim().toLowerCase() : '',
        password,
        firstName.trim(),
        lastName.trim()
      );
      auth?.setUser(response.user);
      navigate('/cards', { replace: true });
    } catch (err: any) {
      setLoading(false);
      
      // Handle field-specific errors from backend
      const backendErrors = err?.response?.data || {};
      const newFieldErrors: Record<string, string> = {};
      
      // Map backend field names to frontend field names
      if (backendErrors.email) {
        const emailErr = Array.isArray(backendErrors.email) 
          ? backendErrors.email[0] 
          : backendErrors.email;
        newFieldErrors.email = emailErr;
        // Mark email as touched to show the error
        setTouchedFields(prev => ({ ...prev, email: true }));
      }
      if (backendErrors.confirm_email) {
        newFieldErrors.confirmEmail = Array.isArray(backendErrors.confirm_email) 
          ? backendErrors.confirm_email[0] 
          : backendErrors.confirm_email;
      }
      if (backendErrors.password) {
        newFieldErrors.password = Array.isArray(backendErrors.password) 
          ? backendErrors.password[0] 
          : backendErrors.password;
      }
      if (backendErrors.first_name) {
        newFieldErrors.firstName = Array.isArray(backendErrors.first_name) 
          ? backendErrors.first_name[0] 
          : backendErrors.first_name;
      }
      if (backendErrors.last_name) {
        newFieldErrors.lastName = Array.isArray(backendErrors.last_name) 
          ? backendErrors.last_name[0] 
          : backendErrors.last_name;
      }
      if (backendErrors.username) {
        newFieldErrors.username = Array.isArray(backendErrors.username) 
          ? backendErrors.username[0] 
          : backendErrors.username;
      }
      
      // Set field errors if any
      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(newFieldErrors);
      }
      
      // Set general error message
      const generalError = 
        backendErrors.detail ||
        backendErrors.non_field_errors?.[0] ||
        (Object.keys(newFieldErrors).length === 0 
          ? 'Registration failed. Please check your details and try again.'
          : 'Please fix the errors below and try again.');
      setError(generalError);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CreditCardIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Credit Card Optimizer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create your account
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Sign up
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Register with your name, email, and a secure password.
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              <Box component="form" onSubmit={onSubmit}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => handleFieldChange('firstName', e.target.value, setFirstName)}
                      onBlur={(e) => handleFieldBlur('firstName', e.target.value)}
                      fullWidth
                      required
                      error={Boolean(fieldErrors.firstName && touchedFields.firstName)}
                      helperText={touchedFields.firstName ? fieldErrors.firstName : ''}
                    />
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => handleFieldChange('lastName', e.target.value, setLastName)}
                      onBlur={(e) => handleFieldBlur('lastName', e.target.value)}
                      fullWidth
                      required
                      error={Boolean(fieldErrors.lastName && touchedFields.lastName)}
                      helperText={touchedFields.lastName ? fieldErrors.lastName : ''}
                    />
                  </Stack>
                  <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => handleFieldChange('username', e.target.value, setUsername)}
                    onBlur={(e) => handleFieldBlur('username', e.target.value)}
                    autoComplete="username"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.username && touchedFields.username)}
                    helperText={
                      touchedFields.username 
                        ? fieldErrors.username 
                        : 'Letters, underscores, and hyphens only. No numbers allowed.'
                    }
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => handleFieldChange('email', e.target.value, setEmail)}
                    onBlur={(e) => handleFieldBlur('email', e.target.value)}
                    autoComplete="email"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.email && touchedFields.email)}
                    helperText={
                      touchedFields.email 
                        ? fieldErrors.email 
                        : 'Emails are validated using strict format checks, Gmail normalization, and domain verification (MX record).'
                    }
                  />
                 
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => handleFieldChange('password', e.target.value, setPassword)}
                    onBlur={(e) => handleFieldBlur('password', e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.password && touchedFields.password)}
                    helperText={
                      touchedFields.password 
                        ? fieldErrors.password 
                        : 'Use at least 8 characters, including upper, lower, number, and special character.'
                    }
                  />
                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => handleFieldChange('confirmPassword', e.target.value, setConfirmPassword)}
                    onBlur={(e) => handleFieldBlur('confirmPassword', e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.confirmPassword && touchedFields.confirmPassword)}
                    helperText={touchedFields.confirmPassword ? fieldErrors.confirmPassword : ''}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      loading ||
                      !username ||
                      !email ||
                      !password ||
                      !confirmPassword ||
                      !firstName ||
                      !lastName
                    }
                    startIcon={<PersonAddIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                      py: 1.3,
                    }}
                  >
                    {loading ? 'Creating account…' : 'Sign up'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/login')}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Already have an account? Sign in
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Signup;

